/**
 * In-app issue reports (#1062). The browser sends each report to Sentry User
 * Feedback (telemetry, replay, screenshots) and then here, where the backend
 * files it as a GitHub issue so reporters never need a GitHub account.
 */
import axios from 'axios';
import { api } from './api';
import { FULL_VERSION } from '../utils/appVersion';

export type FeedbackKind = 'problem' | 'idea';

export interface FeedbackReportFields {
  kind: FeedbackKind;
  summary: string;
  /** Problem: what happened. Idea: what they would like. */
  details: string;
  steps: string;
  expected: string;
  benefit: string;
}

/** Fields shown per kind, in order, with the heading used in plain-text copies. */
export const REPORT_SECTIONS: Record<FeedbackKind, Array<[keyof FeedbackReportFields, string]>> = {
  problem: [
    ['details', 'What happened'],
    ['steps', 'Steps to reproduce'],
    ['expected', 'What I expected'],
  ],
  idea: [
    ['details', "What I'd like"],
    ['benefit', 'Why it would help'],
  ],
};

/** Plain-text rendering for the Sentry feedback message. */
export function reportAsText(fields: FeedbackReportFields): string {
  const label = fields.kind === 'problem' ? 'Problem' : 'Idea';
  const sections = REPORT_SECTIONS[fields.kind]
    .filter(([key]) => fields[key].trim())
    .map(([key, heading]) => `${heading}:\n${fields[key].trim()}`);
  return [`[${label}] ${fields.summary.trim()}`, ...sections].join('\n\n');
}

export type FileReportOutcome = 'filed' | 'not-configured';

/** File the report as a GitHub issue. Resolves 'not-configured' when the
 * tenant has no GitHub token (503); rejects on any other failure. */
export async function fileFeedbackReport(
  fields: FeedbackReportFields,
  screenshots: File[],
  sentryEventId: string | null,
): Promise<FileReportOutcome> {
  const form = new FormData();
  for (const [key, value] of Object.entries(fields)) {
    form.append(key, value.trim());
  }
  form.append('page_url', window.location.href);
  form.append('app_version', FULL_VERSION);
  form.append('viewport', `${window.innerWidth}x${window.innerHeight}`);
  if (sentryEventId) form.append('sentry_event_id', sentryEventId);
  for (const file of screenshots) form.append('screenshots', file, file.name);

  try {
    await api.post('/feedback/report/', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return 'filed';
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 503) return 'not-configured';
    throw error;
  }
}
